import _objectDestructuringEmpty from '@babel/runtime/helpers/objectDestructuringEmpty';
import _extends from '@babel/runtime/helpers/extends';
import _objectWithoutPropertiesLoose from '@babel/runtime/helpers/objectWithoutPropertiesLoose';
import * as React from 'react';
import { Link as Link$1, useInRouterContext } from 'react-router-dom-v5-compat';
import { isClientSideUrl, getClickableBehavior } from '@khanacademy/wonder-blocks-clickable';
import { StyleSheet } from 'aphrodite';
import { addStyle } from '@khanacademy/wonder-blocks-core';
import { spacing, semanticColor, color, border } from '@khanacademy/wonder-blocks-tokens';
import { PhosphorIcon } from '@khanacademy/wonder-blocks-icon';
import externalLinkIcon from '@phosphor-icons/core/bold/arrow-square-out-bold.svg';

const _excluded$1 = ["children", "skipClientNav", "focused", "hovered", "href", "inline", "light", "pressed", "style", "testId", "waiting", "target", "startIcon", "endIcon"];
const StyledA = addStyle("a");
const StyledLink = addStyle(Link$1);
const LinkCore = React.forwardRef(function LinkCore(props, ref) {
  const inRouterContext = useInRouterContext();
  const {
      children,
      skipClientNav,
      focused,
      href,
      inline = false,
      light = false,
      pressed,
      style,
      testId,
      target,
      startIcon,
      endIcon
    } = props,
    restProps = _objectWithoutPropertiesLoose(props, _excluded$1);
  const linkStyles = _generateStyles(inline, light);
  const defaultStyles = [sharedStyles.shared, linkStyles.rest, inline && linkStyles.restInline, !pressed && focused && linkStyles.focus];
  const commonProps = _extends({
    "data-testid": testId,
    style: [defaultStyles, style],
    target
  }, restProps);
  const linkUrl = new URL(href, window.location.origin);
  const isExternalLink = linkUrl.origin !== window.location.origin;
  const externalIcon = React.createElement(PhosphorIcon, {
    icon: externalLinkIcon,
    size: "small",
    style: [linkContentStyles.endIcon, linkContentStyles.centered],
    testId: "external-icon"
  });
  let startIconElement;
  let endIconElement;
  if (startIcon) {
    startIconElement = React.cloneElement(startIcon, _extends({
      style: [linkContentStyles.startIcon, linkContentStyles.centered],
      testId: "start-icon",
      "aria-hidden": "true"
    }, startIcon.props));
  }
  if (endIcon) {
    endIconElement = React.cloneElement(endIcon, _extends({
      style: [linkContentStyles.endIcon, linkContentStyles.centered],
      testId: "end-icon",
      "aria-hidden": "true"
    }, endIcon.props));
  }
  const linkContent = React.createElement(React.Fragment, null, startIcon && startIconElement, children, endIcon ? endIconElement : isExternalLink && target === "_blank" && externalIcon);
  return inRouterContext && !skipClientNav && isClientSideUrl(href) ? React.createElement(StyledLink, _extends({}, commonProps, {
    to: href,
    ref: ref
  }), linkContent) : React.createElement(StyledA, _extends({}, commonProps, {
    href: href,
    ref: ref
  }), linkContent);
});
const styles = {};
const linkContentStyles = StyleSheet.create({
  startIcon: {
    marginInlineEnd: spacing.xxxSmall_4
  },
  endIcon: {
    marginInlineStart: spacing.xxxSmall_4
  },
  centered: {
    verticalAlign: "-10%"
  }
});
const sharedStyles = StyleSheet.create({
  shared: {
    cursor: "pointer",
    textDecoration: "none",
    outline: "none",
    alignItems: "center"
  }
});
const action = semanticColor.action.secondary.progressive;
const theme = {
  color: {
    default: {
      rest: {
        foreground: action.default.foreground
      },
      hover: {
        foreground: action.hover.foreground
      },
      focus: {
        border: semanticColor.focus.outer,
        foreground: action.hover.foreground
      },
      press: {
        foreground: action.press.foreground
      }
    },
    inverse: {
      rest: {
        foreground: semanticColor.text.inverse
      },
      hover: {
        foreground: semanticColor.text.inverse
      },
      focus: {
        border: semanticColor.border.inverse,
        foreground: semanticColor.text.inverse
      },
      press: {
        foreground: color.fadedBlue
      }
    }
  }
};
const _generateStyles = (inline, light) => {
  const buttonType = `${inline.toString()}-${light.toString()}`;
  if (styles[buttonType]) {
    return styles[buttonType];
  }
  const variant = light ? theme.color.inverse : theme.color.default;
  const focusStyling = {
    color: variant.focus.foreground,
    outline: `${border.width.thin} solid ${variant.focus.border}`,
    borderRadius: border.radius.radius_040
  };
  const pressStyling = {
    color: variant.press.foreground,
    textDecoration: "underline currentcolor solid"
  };
  const newStyles = {
    rest: {
      color: variant.rest.foreground,
      ":hover": {
        textDecoration: "underline currentcolor solid",
        color: variant.hover.foreground
      },
      ":focus-visible": focusStyling,
      ":active": pressStyling
    },
    restInline: {
      textDecoration: "underline currentcolor solid",
      textUnderlineOffset: 2
    },
    focus: focusStyling,
    press: pressStyling
  };
  styles[buttonType] = StyleSheet.create(newStyles);
  return styles[buttonType];
};

const _excluded = ["onClick", "beforeNav", "safeWithNav", "href", "skipClientNav", "children", "tabIndex", "onKeyDown", "onKeyUp", "target", "inline", "light"];
const Link = React.forwardRef(function Link(props, ref) {
  const {
      onClick,
      beforeNav = undefined,
      safeWithNav,
      href,
      skipClientNav,
      children,
      tabIndex,
      onKeyDown,
      onKeyUp,
      target = undefined,
      inline = false,
      light = false
    } = props,
    sharedProps = _objectWithoutPropertiesLoose(props, _excluded);
  const inRouterContext = useInRouterContext();
  const ClickableBehavior = getClickableBehavior(href, skipClientNav, inRouterContext);
  if (beforeNav) {
    return React.createElement(ClickableBehavior, {
      disabled: false,
      href: href,
      role: "link",
      onClick: onClick,
      beforeNav: beforeNav,
      safeWithNav: safeWithNav,
      onKeyDown: onKeyDown,
      onKeyUp: onKeyUp
    }, (state, _ref) => {
      let childrenProps = _extends({}, (_objectDestructuringEmpty(_ref), _ref));
      return React.createElement(LinkCore, _extends({}, sharedProps, state, childrenProps, {
        skipClientNav: skipClientNav,
        href: href,
        target: target,
        tabIndex: tabIndex,
        inline: inline,
        light: light,
        ref: ref
      }), children);
    });
  } else {
    return React.createElement(ClickableBehavior, {
      disabled: false,
      href: href,
      role: "link",
      onClick: onClick,
      safeWithNav: safeWithNav,
      target: target,
      onKeyDown: onKeyDown,
      onKeyUp: onKeyUp
    }, (state, _ref2) => {
      let childrenProps = _extends({}, (_objectDestructuringEmpty(_ref2), _ref2));
      return React.createElement(LinkCore, _extends({}, sharedProps, state, childrenProps, {
        skipClientNav: skipClientNav,
        href: href,
        target: target,
        tabIndex: tabIndex,
        inline: inline,
        light: light,
        ref: ref
      }), children);
    });
  }
});

export { Link as default };
